import React, { useState } from 'react';
import commonSelector from '@store/common/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { useDefaultLandingPageStore } from '@globalStore/defaultLandingPageStore';
import { useDefaultLandingPageMutation } from '@hooks/useDefaultLandingPage';
import DefaultLandingPrompt from '@components/common/DefaultLandingPage';
import featureNames from '@utils/featureToggle/constants';
import ToggleIcon from '@icons/common/toggle.svg';
import { SharingAdapter } from '@components/common/Sharing';
import { PAGE_TYPE, EXPORT_ML } from '@utils/constants';
import { sharingSelector } from '@store/metricLens/sharing/selectors';
import { liveModeSelector } from '@store/metricLens/base/selectors';
import * as dashboardsUIActions from '@store/dashboards/ui/actions';
import RemoveDashboardPopup from '@components/common/Dashboard/RemoveDashboardPopup';

import { setKebabMenuOptions } from '@components/common/kebabMenuUtils';
import {
  onSetDefaultLandingApply,
  onSetDefaultLanding,
} from '@components/common/kebabMenuUtils/defaultLandingPage';

import { exportData } from '@store/export/actions';
import { MarginLayout } from './styled';
import { Dropdown } from 'antd';
import useExport from '@components/export/useExport';
import { metricLensExportConfig } from '@components/export/utils';
import { ToggleButton } from '@src/components/common/Utils/Buttons';

const { baseUrl } = window.conviva;
// eslint-disable-next-line react/display-name
export default ({ currentShareId, pageType }) => {
  const dispatch = useDispatch();
  const { features, profiles } = useSelector(commonSelector);

  // default landing page using Zustand
  const defaultLandingPage = useDefaultLandingPageStore((state) => ({
    url: state.url,
    path: state.path,
    name: state.name,
  }));

  // Use the hook to get the mutation function
  const { setDefaultLandingPage: mutateLandingPage } = useDefaultLandingPageMutation();

  const { pathname } = window.location;
  const defaultPathname = defaultLandingPage.path || '';
  const isDefaultLandingPage = defaultPathname.endsWith(pathname);
  const cid = profiles.filter((p) => p.is_selected)[0].customer_id;
  const dlpOkFn = () => onSetDefaultLandingApply(dispatch, cid, mutateLandingPage);
  const setLandingPageEnabled =
    (features[featureNames.DEFAULT_LANDING_PAGE] || true) && pageType !== PAGE_TYPE.SHARING;

  // share feature
  const isMLShareEnabled = true; // features[featureNames.ML_SHARE];
  const [isShareClicked, setShareClicked] = useState(false);
  const sharingState = useSelector(sharingSelector);
  const setShareButtonState = (flag) => {
    setShareClicked(flag);
  };

  // save feature
  const isMLSaveEnabled = true; // features[featureNames.OVB_SAVE];
  const [isSaveClicked, setSaveClicked] = useState(true);

  const { triggerDownload } = useExport({ config: metricLensExportConfig });

  // export
  const live = useSelector(liveModeSelector);

  const kebabItems = React.useMemo(() => {
    const kebabs = setKebabMenuOptions(
      isDefaultLandingPage,
      setLandingPageEnabled,
      isMLShareEnabled,
      isMLSaveEnabled,
      pageType,
    );
    // kebabs.push({
    //   key: 'export',
    //   label: 'Export',
    //   disabled: live,
    // });
    kebabs.push({
      key: 'export-frontend',
      label: 'Export',
      disabled: live,
    });
    return kebabs;
  }, [setLandingPageEnabled, isDefaultLandingPage, live]);

  const onApply = ({ key: kebabId }: any) => {
    switch (kebabId) {
      case 'set-default-landing-page': {
        onSetDefaultLanding(dispatch, defaultLandingPage, cid, mutateLandingPage);
        break;
      }
      case 'share': {
        setShareClicked(true);
        break;
      }
      case 'save': {
        if (pageType === PAGE_TYPE.SHARING) {
          dispatch(dashboardsUIActions.changeSaveDashboardPromptVisibility(true, currentShareId));
        } else {
          dispatch(dashboardsUIActions.toggleUpdatePopupVisible(true, currentShareId));
        }
        setSaveClicked(!isSaveClicked);
        break;
      }
      case 'export': {
        dispatch(exportData(EXPORT_ML));
        break;
      }
      case 'delete': {
        dispatch(dashboardsUIActions.toggleRemovePopupVisible());
        break;
      }
      case 'export-frontend': {
        triggerDownload();
        break;
      }
    }
  };

  return (
    <React.Fragment>
      <MarginLayout>
        <Dropdown menu={{ items: kebabItems, onClick: onApply }} trigger={['click']}>
          <ToggleButton icon={<ToggleIcon />} />
        </Dropdown>
      </MarginLayout>
      <DefaultLandingPrompt />
      {isShareClicked && (
        <SharingAdapter
          isShareClicked={isShareClicked}
          config={sharingState}
          url={`${baseUrl}/dashboard/metriclens/sharing`}
          setShareClicked={setShareButtonState}
        />
      )}

      <RemoveDashboardPopup id={currentShareId} />
    </React.Fragment>
  );
};
